import {
  Eye,
  FileText,
  MagnifyingGlass,
  Pencil,
  Trash,
} from '@phosphor-icons/react';
import { useDebounce } from 'ahooks';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Modal } from 'src/components/Modal';
import { SideDropActions } from 'src/components/SideDropActions';
import Table from 'src/components/Table';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, can, Subject } from 'src/hooks/usePermissions';
import {
  KnowledgeBase,
  KnowledgeBaseRowItem,
} from 'src/interfaces/knowledge-base.interface';
import { StabilityLevel } from 'src/types/stability-level';

const headers = [
  { key: 'id', label: 'ID', width: '8rem' },
  { key: 'label', label: 'Name', width: '60%' },
  { key: 'action', label: '', width: '20%', disableSorting: true },
];

export const ListKnowledgeBases = () => {
  const { get, post, remove } = useAuthedRequest();
  const notification = useNotification();

  const { role } = useEnvironment();

  const navigate = useNavigate();

  const [rows, setRows] = useState<KnowledgeBaseRowItem[]>([]);
  const [knowledgeBaseName, setKnowledgeBaseName] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalCreateOpen, setModalCreateOpen] = useState(false);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);

    const data = await get(`/knowledge-bases`);

    const knowledgeBases = data.items.map((item: KnowledgeBase) => ({
      id: <CopyableField value={item.id} notification={notification} />,
      label: item.label,
      action: (
        <div
          className="flex justify-center items-center"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <SideDropActions
            options={[
              {
                label: can(role, 'update', Subject.KNOWLEDGE_BASES)
                  ? 'Edit'
                  : 'View',
                Icon: item.user_id ? Pencil : Eye,
                onClick: () => navigate(`/knowledge-bases/${item.id}`),
              },
              {
                label: 'Delete',
                Icon: Trash,
                color: 'error' as const,
                show: can(role, 'delete', Subject.KNOWLEDGE_BASES),
                onClick: () => openDeleteKnowledgeBase(item),
              },
            ]}
          />
        </div>
      ),
    }));

    setRows(knowledgeBases);
    setTotalItems(knowledgeBases.length);
    setLoading(false);
  };

  useEffect(() => {
    if (!loading) {
      fetchData(currentPage, size);
    }
  }, [currentPage, sortConfig, size]);

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
    fetchData(1, perPage);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const submitFilter = () => {
    handlePageChange(1, size);
  };

  const openDeleteKnowledgeBase = (knowledgeBase: KnowledgeBase) => {
    setModalDeleteOpen(true);
    setSelectedId(knowledgeBase.id);
  };

  const handleCreateKnowledgeBase = async () => {
    setLoading(true);
    try {
      await post(`/knowledge-bases`, { label: knowledgeBaseName });
      notification.success(
        'Knowledge base scheduled for creation. Please check back in a few minutes.',
        { persist: true },
      );
      setModalCreateOpen(false);
    } catch {
      notification.error('Failed to create knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveKnowledgeBase = async () => {
    setLoading(true);
    try {
      await remove(`/knowledge-bases/${selectedId}`);
      notification.success(
        'Knowledge base scheduled for deletion. Please check back in a few minutes.',
        { persist: true },
      );
      setModalDeleteOpen(false);
    } catch {
      notification.error('Failed to delete knowledge base');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1">
      <Heading
        title="Knowledge Bases"
        subtitle="Knowledge bases are collections of documents that can be used to answer questions or provide information."
        stabilityLevel={StabilityLevel.Beta}
      >
        <div className="flex">
          <Button
            className="w-42 border-none flex items-center justify-center text-neutral-500"
            variant="outlined"
            color="default"
            href="https://docs.fluents.ai/api-reference/knowledge-bases"
            target="_blank"
          >
            View help doc
            <FileText className="ml-2" size={18} />
          </Button>
        </div>
      </Heading>

      <ContentWrapper>
        <div className="flex flex-col md:flex-row justify-end mb-6">
          <Can operation="create" subject={Subject.KNOWLEDGE_BASES}>
            <Button onClick={() => setModalCreateOpen(true)}>
              Create Knowledge Base
            </Button>
          </Can>
        </div>

        <Can operation="read" subject={Subject.KNOWLEDGE_BASES}>
          <Table
            headers={headers}
            rows={rows}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            loading={loading}
            setSize={handleSizeChange}
            defaultSize={size}
            onRowClick={(index: number) => {
              const knowledgeBase = rows[index];
              if (knowledgeBase) {
                navigate(
                  `/knowledge-bases/${(knowledgeBase.id as unknown as { props: { value: string } }).props.value}`,
                );
              }
            }}
          />
        </Can>
      </ContentWrapper>

      <Modal
        title="Create Knowledge Base"
        isOpen={modalCreateOpen}
        onClose={() => {
          setModalCreateOpen(false);
        }}
        actionButton={
          <Button onClick={handleCreateKnowledgeBase}>Create</Button>
        }
        className="w-96"
      >
        <Input
          label="Knowledge Base Name"
          type="text"
          value={knowledgeBaseName}
          onChange={(e) => setKnowledgeBaseName(e.target.value)}
          required
        />
      </Modal>

      <Modal
        title="Delete Knowledge Base"
        isOpen={modalDeleteOpen}
        onClose={() => {
          setModalDeleteOpen(false);
          setSelectedId('');
        }}
        actionButton={
          <Button onClick={handleRemoveKnowledgeBase}>
            Delete Knowledge Base
          </Button>
        }
        className="w-96"
      >
        <div className="text-center">
          Are you sure you want to delete this knowledge base?
        </div>
      </Modal>
    </div>
  );
};
